import React, { useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  Calendar,
  CheckCircle2,
  Clock,
  Loader2,
  MessageSquare,
  Sparkles,
  Trash2,
  Upload,
  Video,
  Wand2,
  X,
} from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { buildApiUrl } from '../../../lib/apiBase';

const formatDateTime = (value) => {
  if (!value) return 'Not scheduled';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getTimeValue = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const getStatusBadge = (status) => {
  switch (status) {
    case 'draft_ready':
      return 'border-yellow-200 bg-yellow-100 text-yellow-900 dark:border-yellow-500/30 dark:bg-yellow-500/15 dark:text-yellow-100';
    case 'published':
      return 'border-green-200 bg-green-100 text-green-900 dark:border-green-500/30 dark:bg-green-500/15 dark:text-green-100';
    case 'auto_published':
      return 'border-blue-200 bg-blue-100 text-blue-900 dark:border-blue-500/30 dark:bg-blue-500/15 dark:text-blue-100';
    default:
      return 'border-slate-200 bg-slate-100 text-slate-800 dark:border-slate-500/30 dark:bg-slate-500/15 dark:text-slate-100';
  }
};

const getStatusLabel = (status) => {
  switch (status) {
    case 'draft_ready':
      return 'Needs Review';
    case 'published':
      return 'Published';
    case 'auto_published':
      return 'Auto-Published';
    default:
      return 'Imported';
  }
};

const ATTENTION_BADGE_CLASS = 'border-amber-200 bg-amber-100 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-100';
const RECOMMENDED_BADGE_CLASS = 'border-yellow-200 bg-yellow-100 text-yellow-900 dark:border-yellow-500/30 dark:bg-yellow-500/15 dark:text-yellow-100';
const LOCAL_DEV_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);

const isLocalDevHost = () => {
  if (typeof window === 'undefined') return false;
  return LOCAL_DEV_HOSTS.has(window.location.hostname);
};

const getRecordingMediaUrl = (recording) => {
  const candidate = recording?.playback_url || recording?.source_url;
  if (!candidate) return null;

  if (
    typeof window !== 'undefined'
    && window.location.hostname === '127.0.0.1'
    && candidate.startsWith('/api/recordings/')
  ) {
    return `http://127.0.0.1:8000${candidate}`;
  }

  if (
    typeof window !== 'undefined'
    && window.location.hostname === 'localhost'
    && candidate.startsWith('/api/recordings/')
  ) {
    return `http://localhost:8000${candidate}`;
  }

  return buildApiUrl(candidate);
};

const RecordingsTab = ({
  viewMode,
  setViewMode,
  focusFilter,
  setFocusFilter,
  recordings,
  loading,
  uploading,
  uploadProgress,
  showUploadModal,
  setShowUploadModal,
  uploadForm,
  setUploadForm,
  selectedFile,
  setSelectedFile,
  fileInputRef,
  focusAreaOrder,
  getFocusAreaLabel,
  handleFileSelect,
  handleUpload,
  analyzingRecordings,
  recordingInsights,
  runRecordingAnalysis,
  handleSaveReview,
  handlePublishFocus,
  handleDeleteRecording,
  handleReplaceRecordingMedia,
  replacingRecordingMediaId,
  activeZone,
  pendingFocusReviewCount,
  onOpenTool,
}) => {
  const [selectedRecordingId, setSelectedRecordingId] = useState(null);
  const [savingReview, setSavingReview] = useState(false);
  const [publishingFocus, setPublishingFocus] = useState(false);
  const [deletingRecording, setDeletingRecording] = useState(false);
  const [replacementPickerKey, setReplacementPickerKey] = useState(0);
  const [replacementPreviewNonceById, setReplacementPreviewNonceById] = useState({});
  const [draftForm, setDraftForm] = useState({
    title: '',
    guest_guide_name: '',
    date: '',
    duration: '',
    focus_area: 'general',
    description: '',
    transcript: '',
    summary: '',
    key_insights: ['', '', ''],
    candidate_prompts: ['', '', ''],
    recommended_prompt_index: 0,
    final_prompt_index: 0,
    custom_note: '',
    email_audience: 'founding_40_all',
  });

  const filteredRecordings = useMemo(() => {
    return recordings.filter((rec) => {
      if (focusFilter !== 'all' && rec.focus_area !== focusFilter) return false;
      return true;
    }).sort((a, b) => {
      if (viewMode === 'recent') return new Date(b.date) - new Date(a.date);
      if (viewMode === 'drafts') {
        const aReady = a.status === 'draft_ready' ? 1 : 0;
        const bReady = b.status === 'draft_ready' ? 1 : 0;
        if (aReady !== bReady) return bReady - aReady;
      }
      if (viewMode === 'published') {
        const aPublished = ['published', 'auto_published'].includes(a.status) ? 1 : 0;
        const bPublished = ['published', 'auto_published'].includes(b.status) ? 1 : 0;
        if (aPublished !== bPublished) return bPublished - aPublished;
      }
      return new Date(b.date) - new Date(a.date);
    });
  }, [focusFilter, recordings, viewMode]);

  const draftReadyRecordings = useMemo(
    () => recordings.filter((rec) => rec.status === 'draft_ready'),
    [recordings],
  );

  const publishedRecordings = useMemo(
    () => recordings.filter((rec) => ['published', 'auto_published'].includes(rec.status)),
    [recordings],
  );

  const missingTranscriptCount = useMemo(
    () => recordings.filter((rec) => !(rec.transcript || '').trim()).length,
    [recordings],
  );

  const nextReviewDeadline = useMemo(() => {
    return [...draftReadyRecordings]
      .filter((rec) => rec.publish_deadline_at)
      .sort((a, b) => getTimeValue(a.publish_deadline_at) - getTimeValue(b.publish_deadline_at))[0] || null;
  }, [draftReadyRecordings]);

  const selectedRecording = filteredRecordings.find((rec) => rec.recording_id === selectedRecordingId) || filteredRecordings[0] || null;

  useEffect(() => {
    if (!selectedRecordingId && filteredRecordings.length > 0) {
      setSelectedRecordingId(filteredRecordings[0].recording_id);
    }
  }, [filteredRecordings, selectedRecordingId]);

  useEffect(() => {
    if (!selectedRecording) return;
    const analysis = selectedRecording.analysis || {};
    const prompts = (analysis.candidate_prompts || analysis.generated_prompts || []).slice(0, 3);
    const finalPrompt = analysis.final_prompt;
    const finalPromptIndex = finalPrompt ? Math.max(0, prompts.findIndex((prompt) => prompt === finalPrompt)) : (analysis.recommended_prompt_index ?? 0);
    const insights = (analysis.key_insights || analysis.key_points || []).slice(0, 3);

    setDraftForm({
      title: selectedRecording.title || '',
      guest_guide_name: selectedRecording.guest_guide_name || '',
      date: selectedRecording.date ? new Date(selectedRecording.date).toISOString().split('T')[0] : '',
      duration: selectedRecording.duration || '',
      focus_area: selectedRecording.focus_area || 'general',
      description: selectedRecording.description || '',
      transcript: selectedRecording.transcript || '',
      summary: analysis.summary || '',
      key_insights: [...insights, '', '', ''].slice(0, 3),
      candidate_prompts: [...prompts, '', '', ''].slice(0, 3),
      recommended_prompt_index: Math.max(0, Math.min(analysis.recommended_prompt_index ?? 0, 2)),
      final_prompt_index: Math.max(0, Math.min(finalPromptIndex === -1 ? 0 : finalPromptIndex, 2)),
      custom_note: selectedRecording.custom_note || '',
      email_audience: selectedRecording.email_audience || 'founding_40_all',
    });
  }, [selectedRecording]);

  const updateInsight = (index, value) => {
    const next = [...draftForm.key_insights];
    next[index] = value;
    setDraftForm((prev) => ({ ...prev, key_insights: next }));
  };

  const updatePrompt = (index, value) => {
    const next = [...draftForm.candidate_prompts];
    next[index] = value;
    setDraftForm((prev) => ({ ...prev, candidate_prompts: next }));
  };

  const saveReview = async () => {
    if (!selectedRecording) return;
    setSavingReview(true);
    try {
      await handleSaveReview(selectedRecording.recording_id, {
        title: draftForm.title,
        guest_guide_name: draftForm.guest_guide_name,
        date: draftForm.date ? `${draftForm.date}T00:00:00Z` : undefined,
        duration: draftForm.duration,
        focus_area: draftForm.focus_area,
        description: draftForm.description,
        transcript: draftForm.transcript,
        transcript_status: draftForm.transcript ? 'available' : 'missing',
        summary: draftForm.summary,
        key_insights: draftForm.key_insights,
        candidate_prompts: draftForm.candidate_prompts,
        recommended_prompt_index: draftForm.recommended_prompt_index,
        final_prompt_index: draftForm.final_prompt_index,
        custom_note: draftForm.custom_note || null,
        email_audience: draftForm.email_audience || 'all_members',
      });
      toast.success('Recording review saved');
      return true;
    } catch (error) {
      toast.error(error.message || 'Failed to save review');
      throw error;
    } finally {
      setSavingReview(false);
    }
  };

  const publishFocus = async () => {
    if (!selectedRecording) return;
    setPublishingFocus(true);
    try {
      await saveReview();
      const promptText = draftForm.candidate_prompts[draftForm.final_prompt_index];
      await handlePublishFocus(
        selectedRecording.recording_id,
        promptText,
        draftForm.final_prompt_index,
        draftForm.custom_note || null,
        draftForm.email_audience || 'all_members',
      );
      toast.success('Weekly focus published');
    } catch (error) {
      toast.error(error.message || 'Failed to publish weekly focus');
    } finally {
      setPublishingFocus(false);
    }
  };

  const deleteRecording = async () => {
    if (!selectedRecording) return;
    const confirmed = window.confirm(`Delete "${selectedRecording.title}" permanently?`);
    if (!confirmed) return;

    setDeletingRecording(true);
    try {
      await handleDeleteRecording(selectedRecording.recording_id);
      toast.success('Recording deleted');
      setSelectedRecordingId(null);
    } catch (error) {
      toast.error(error.message || 'Failed to delete recording');
    } finally {
      setDeletingRecording(false);
    }
  };

  const chooseReplacementMedia = () => {
    const input = document.getElementById(`recording-replacement-input-${replacementPickerKey}`);
    input?.click();
  };

  const handleReplacementFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedRecording) return;
    try {
      await handleReplaceRecordingMedia(selectedRecording.recording_id, file);
      setReplacementPreviewNonceById((prev) => ({
        ...prev,
        [selectedRecording.recording_id]: Date.now(),
      }));
    } finally {
      event.target.value = '';
      setReplacementPickerKey((value) => value + 1);
    }
  };

  const selectedRecordingStatusLabel = getStatusLabel(selectedRecording?.status);
  const selectedRecordingIsPublished = ['published', 'auto_published'].includes(selectedRecording?.status);
  const selectedRecordingMediaUrlBase = getRecordingMediaUrl(selectedRecording);
  const selectedRecordingPreviewNonce = selectedRecording ? replacementPreviewNonceById[selectedRecording.recording_id] : null;
  const selectedRecordingMediaUrl = selectedRecordingMediaUrlBase
    ? `${selectedRecordingMediaUrlBase}${selectedRecordingMediaUrlBase.includes('?') ? '&' : '?'}v=${selectedRecordingPreviewNonce || 'current'}`
    : null;
  const selectedRecordingIsAudio = (selectedRecording?.file_type || '').startsWith('audio/');
  const selectedRecordingNeedsBucketPlayback = Boolean(selectedRecording?.storage_key && !selectedRecording?.file_path);
  const showLocalPlaybackNote = isLocalDevHost() && selectedRecordingNeedsBucketPlayback;

  return (
    <div className="space-y-6">
      <Card className="border-emerald-200 bg-gradient-to-br from-[#eef9f1] via-white to-[#eef6f2] p-4 dark:border-emerald-500/15 dark:bg-gradient-to-br dark:from-[#122018] dark:via-[#11161a] dark:to-[#101513]">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-emerald-700 dark:text-emerald-300">
              {activeZone?.id === 'heart-work' ? 'Dad Partners flow' : 'Recordings flow'}
            </p>
            <h3 className="mt-2 text-xl font-semibold text-slate-900 dark:text-white">
              Review the story, sharpen the takeaway, then publish one clear weekly focus
            </h3>
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
              This tool should feel like a review desk, not just a media library. Start with the draft that needs a decision soonest, tighten the summary and prompts, and only publish when the weekly focus is clear enough to carry real care.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenTool?.('feedback', 'listen-learn')}
                className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                Review feedback
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenTool?.('resources', 'support-dads')}
                className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
              >
                <BookOpen className="mr-2 h-4 w-4" />
                Open resources
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={runRecordingAnalysis}
                disabled={analyzingRecordings || recordings.length === 0}
                className="border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
              >
                {analyzingRecordings ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                {analyzingRecordings ? 'Analyzing...' : 'Analyze library'}
              </Button>
            </div>
          </div>

          <div className="grid min-w-full gap-3 sm:grid-cols-2 xl:min-w-[380px] xl:max-w-[460px]">
            <div className="rounded-2xl border border-amber-200 bg-white/90 p-4 dark:border-amber-500/20 dark:bg-black/20">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">Drafts ready</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">{pendingFocusReviewCount ?? draftReadyRecordings.length}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-gray-300">Recordings still waiting on a final weekly focus decision</p>
            </div>
            <div className="rounded-2xl border border-emerald-200 bg-white/90 p-4 dark:border-emerald-500/20 dark:bg-black/20">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">Published</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">{publishedRecordings.length}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-gray-300">Recordings already live in the member-facing focus archive</p>
            </div>
            <div className="rounded-2xl border border-cyan-200 bg-white/90 p-4 dark:border-cyan-500/20 dark:bg-black/20">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">Next deadline</p>
              <p className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">
                {nextReviewDeadline ? formatDateTime(nextReviewDeadline.publish_deadline_at) : 'No draft waiting'}
              </p>
              <p className="mt-2 text-sm text-slate-600 dark:text-gray-300">
                {nextReviewDeadline ? `${nextReviewDeadline.title || 'Untitled recording'} should be decided next` : 'Nothing is pressuring the queue right now'}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 dark:border-white/10 dark:bg-black/20">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">Missing transcript</p>
              <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">{missingTranscriptCount}</p>
              <p className="mt-2 text-sm text-slate-600 dark:text-gray-300">Recordings that may need more review context before publishing</p>
            </div>
          </div>
        </div>

        {recordingInsights ? (
          <div className="mt-4 rounded-2xl border border-emerald-200 bg-white/80 p-3 text-sm text-slate-600 dark:border-emerald-500/20 dark:bg-black/20 dark:text-gray-300">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-700 dark:text-emerald-300">
              Analysis in view
            </p>
            <p className="mt-2">
              {recordingInsights.topRated || recordingInsights.mostViewed || 'Recording analysis is ready to inform the next support move.'}
            </p>
          </div>
        ) : null}
      </Card>

      <Card className="border-slate-200 bg-white p-4 dark:border-[#2a2a32] dark:bg-[#1e1e24]">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-aqua" />
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">Upload Sunday Zoom Recording</h3>
          </div>
          <p className="text-xs text-slate-500 dark:text-gray-400">MP4, WEBM, MOV, MP3, WAV • Max 2GB</p>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="video/mp4,video/webm,video/quicktime,audio/mpeg,audio/wav"
          className="hidden"
        />
        <div
          onClick={() => fileInputRef.current?.click()}
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-left transition-colors hover:border-aqua/30 dark:border-[#2a2a32] dark:bg-[#16161a]"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-aqua/10">
              <Video className="h-5 w-5 text-aqua" />
            </div>
            <div>
              <p className="font-medium text-slate-900 dark:text-white">Drop your Zoom recording here or click to browse</p>
              <p className="text-sm text-slate-500 dark:text-gray-400">Create the draft first, then tighten the summary, choose the final prompt, and publish when it is ready.</p>
            </div>
          </div>
          <Button className="bg-aqua hover:bg-aqua-dark text-charcoal">Choose File</Button>
        </div>
      </Card>

      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-[#1e1e24] border-[#2a2a32] p-6 w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Prepare Zoom Recording Draft</h3>
              <button onClick={() => { setShowUploadModal(false); setSelectedFile(null); }} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="p-3 bg-[#16161a] rounded-lg">
                <p className="text-white text-sm truncate">{selectedFile?.name}</p>
                <p className="text-gray-500 text-xs">{(selectedFile?.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Title *</label>
                  <Input
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                    className="bg-[#16161a] border-[#2a2a32] text-white"
                    placeholder="Recording title"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Guide Partner</label>
                  <Input
                    value={uploadForm.guest_guide_name}
                    onChange={(e) => setUploadForm({ ...uploadForm, guest_guide_name: e.target.value })}
                    className="bg-[#16161a] border-[#2a2a32] text-white"
                    placeholder="Guide Partner name"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Zoom Date</label>
                  <Input
                    type="date"
                    value={uploadForm.date}
                    onChange={(e) => setUploadForm({ ...uploadForm, date: e.target.value })}
                    className="bg-[#16161a] border-[#2a2a32] text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Focus Area</label>
                  <select
                    value={uploadForm.focus_area}
                    onChange={(e) => setUploadForm({ ...uploadForm, focus_area: e.target.value })}
                    className="w-full bg-[#16161a] border border-[#2a2a32] text-white rounded-lg px-3 py-2"
                  >
                    {focusAreaOrder.map((id) => (
                      <option key={id} value={id}>{getFocusAreaLabel(id)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Duration</label>
                  <Input
                    value={uploadForm.duration}
                    onChange={(e) => setUploadForm({ ...uploadForm, duration: e.target.value })}
                    className="bg-[#16161a] border-[#2a2a32] text-white"
                    placeholder="e.g., 58 min"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Short Description</label>
                <Textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  className="bg-[#16161a] border-[#2a2a32] text-white"
                  placeholder="A short note about the conversation..."
                  rows={2}
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Transcript</label>
                <Textarea
                  value={uploadForm.transcript}
                  onChange={(e) => setUploadForm({ ...uploadForm, transcript: e.target.value })}
                  className="bg-[#16161a] border-[#2a2a32] text-white min-h-[180px]"
                  placeholder="Paste the Zoom transcript here when available. The draft can still be created without it."
                />
              </div>
              {uploading && (
                <div className="w-full bg-[#16161a] rounded-full h-2">
                  <div className="bg-aqua h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setShowUploadModal(false); setSelectedFile(null); }}
                  className="flex-1 border-[#2a2a32] text-white hover:bg-[#2a2a32]"
                  disabled={uploading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={uploading || !uploadForm.title}
                  className="flex-1 bg-aqua hover:bg-aqua-dark text-charcoal"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Wand2 className="w-4 h-4 mr-2" />}
                  {uploading ? 'Creating draft...' : 'Upload and Generate Draft'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-4">
        <div className="flex flex-wrap items-center gap-4">
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            className="bg-[#16161a] border border-[#2a2a32] text-white rounded-lg px-3 py-2"
          >
            <option value="all">All Recordings</option>
            <option value="drafts">Needs Review First</option>
            <option value="published">Published First</option>
            <option value="recent">Most Recent</option>
          </select>
          <select
            value={focusFilter}
            onChange={(e) => setFocusFilter(e.target.value)}
            className="bg-[#16161a] border border-[#2a2a32] text-white rounded-lg px-3 py-2"
          >
            <option value="all">All Focus Areas</option>
            {focusAreaOrder.map((id) => (
              <option key={id} value={id}>{getFocusAreaLabel(id)}</option>
            ))}
          </select>
        </div>
      </Card>

      <div className="grid xl:grid-cols-[360px,1fr] gap-6">
        <Card className="bg-[#1e1e24] border-[#2a2a32] p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Review Queue</h3>
            <Badge className="bg-aqua/10 text-aqua border-aqua/30">{filteredRecordings.length}</Badge>
          </div>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-aqua animate-spin" />
            </div>
          ) : filteredRecordings.length === 0 ? (
            <div className="text-center py-8">
              <Video className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No recordings yet. Upload your first Zoom call above.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredRecordings.map((rec) => (
                <button
                  key={rec.recording_id}
                  type="button"
                  onClick={() => setSelectedRecordingId(rec.recording_id)}
                  className={`w-full text-left p-4 rounded-xl border transition-colors ${
                    rec.recording_id === (selectedRecording?.recording_id || selectedRecordingId)
                      ? 'border-aqua bg-aqua/10'
                      : 'border-[#2a2a32] bg-[#16161a] hover:border-aqua/30'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-white font-medium">{rec.title}</p>
                      <p className="text-gray-500 text-sm mt-1">
                        {rec.guest_guide_name || 'Guide Partner TBD'} • {new Date(rec.date).toLocaleDateString()}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {getStatusLabel(rec.status)}
                        {rec.updated_at ? ` • Updated ${new Date(rec.updated_at).toLocaleDateString()}` : ''}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {rec.publish_deadline_at ? (
                          <Badge className={ATTENTION_BADGE_CLASS}>
                            Deadline {formatDateTime(rec.publish_deadline_at)}
                          </Badge>
                        ) : null}
                        {!(rec.transcript || '').trim() ? (
                          <Badge className="border-slate-500/30 bg-slate-500/10 text-slate-300">
                            Transcript missing
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                    <Badge className={getStatusBadge(rec.status)}>{getStatusLabel(rec.status)}</Badge>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
          {!selectedRecording ? (
            <div className="text-center py-16">
              <Sparkles className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Select a recording to review its focus draft.</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-aqua/80 mb-2">Recording Review</p>
                  <h3 className="text-2xl font-bold text-white">{selectedRecording.title}</h3>
                  <div className="flex flex-wrap gap-3 mt-3 text-sm text-gray-400">
                    <span className="inline-flex items-center gap-2"><Calendar className="w-4 h-4 text-aqua" /> {new Date(selectedRecording.date).toLocaleDateString()}</span>
                    <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-aqua" /> Auto-publish deadline: {formatDateTime(selectedRecording.publish_deadline_at)}</span>
                  </div>
                  <p className="mt-3 text-sm text-gray-400">
                    {selectedRecordingIsPublished
                      ? 'These edits update the existing published focus without sending it back to draft.'
                      : 'Save the draft as you refine the story, then publish when the weekly focus is final.'}
                  </p>
                </div>
                <Badge className={getStatusBadge(selectedRecording.status)}>{selectedRecordingStatusLabel}</Badge>
              </div>

              <div className="rounded-2xl overflow-hidden border border-[#2a2a32] bg-[#111318]">
                {selectedRecordingMediaUrl ? (
                  selectedRecordingIsAudio ? (
                    <div key={selectedRecordingMediaUrl} className="p-6 sm:p-8">
                      <div className="mb-4 flex items-center gap-3 text-white">
                        <Video className="h-5 w-5 text-aqua" />
                        <span className="font-medium">Listen to the recording</span>
                      </div>
                      <audio controls className="w-full">
                        <source src={selectedRecordingMediaUrl} type={selectedRecording.file_type || 'audio/mpeg'} />
                      </audio>
                    </div>
                  ) : (
                    <video key={selectedRecordingMediaUrl} controls className="w-full max-h-[520px] bg-black">
                      <source src={selectedRecordingMediaUrl} type={selectedRecording.file_type || 'video/mp4'} />
                    </video>
                  )
                ) : (
                  <div className="flex h-64 items-center justify-center text-gray-500">
                    <div className="text-center">
                      <Video className="mx-auto mb-3 h-10 w-10 text-aqua/70" />
                      <p>Recording playback will appear here when media is available.</p>
                    </div>
                  </div>
                )}
              </div>

              {showLocalPlaybackNote ? (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
                  This recording is stored in the recordings bucket. If playback is blank locally, restart the backend with the recordings bucket env loaded.
                </div>
              ) : null}

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Title</label>
                  <Input value={draftForm.title} onChange={(e) => setDraftForm((prev) => ({ ...prev, title: e.target.value }))} className="bg-[#16161a] border-[#2a2a32] text-white" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Guide Partner</label>
                  <Input value={draftForm.guest_guide_name} onChange={(e) => setDraftForm((prev) => ({ ...prev, guest_guide_name: e.target.value }))} className="bg-[#16161a] border-[#2a2a32] text-white" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Zoom Date</label>
                  <Input type="date" value={draftForm.date} onChange={(e) => setDraftForm((prev) => ({ ...prev, date: e.target.value }))} className="bg-[#16161a] border-[#2a2a32] text-white" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Duration</label>
                  <Input value={draftForm.duration} onChange={(e) => setDraftForm((prev) => ({ ...prev, duration: e.target.value }))} className="bg-[#16161a] border-[#2a2a32] text-white" />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Focus Area</label>
                  <select
                    value={draftForm.focus_area}
                    onChange={(e) => setDraftForm((prev) => ({ ...prev, focus_area: e.target.value }))}
                    className="w-full bg-[#16161a] border border-[#2a2a32] text-white rounded-lg px-3 py-2"
                  >
                    {focusAreaOrder.map((id) => (
                      <option key={id} value={id}>{getFocusAreaLabel(id)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">Short Description</label>
                <Textarea value={draftForm.description} onChange={(e) => setDraftForm((prev) => ({ ...prev, description: e.target.value }))} className="bg-[#16161a] border-[#2a2a32] text-white" rows={2} />
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">Transcript</label>
                <Textarea value={draftForm.transcript} onChange={(e) => setDraftForm((prev) => ({ ...prev, transcript: e.target.value }))} className="bg-[#16161a] border-[#2a2a32] text-white min-h-[140px]" />
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">Guide Partner Story Summary</label>
                <Textarea value={draftForm.summary} onChange={(e) => setDraftForm((prev) => ({ ...prev, summary: e.target.value }))} className="bg-[#16161a] border-[#2a2a32] text-white min-h-[110px]" />
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-4 h-4 text-aqua" />
                  <h4 className="text-white font-semibold">3 Key Insights</h4>
                </div>
                <div className="space-y-3">
                  {draftForm.key_insights.map((insight, idx) => (
                    <div key={`insight-${idx}`} className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-aqua text-charcoal font-bold flex items-center justify-center flex-shrink-0 mt-1">{idx + 1}</div>
                      <Textarea
                        value={insight}
                        onChange={(e) => updateInsight(idx, e.target.value)}
                        className="bg-[#16161a] border-[#2a2a32] text-white"
                        rows={2}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-aqua" />
                  <h4 className="font-semibold text-slate-900 dark:text-white">AI Candidate Focus Prompts</h4>
                </div>
                <div className="space-y-3">
                  {draftForm.candidate_prompts.map((prompt, idx) => (
                    <div
                      key={`prompt-${idx}`}
                      className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-[#2a2a32] dark:bg-[#16161a]"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                        <div className="flex items-center gap-3">
                          <Badge className="bg-aqua/10 text-aqua border-aqua/30">Option {idx + 1}</Badge>
                          {draftForm.recommended_prompt_index === idx && (
                            <Badge className={RECOMMENDED_BADGE_CLASS}>AI Recommended</Badge>
                          )}
                        </div>
                        <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-gray-300">
                          <input
                            type="radio"
                            name="final_prompt"
                            checked={draftForm.final_prompt_index === idx}
                            onChange={() => setDraftForm((prev) => ({ ...prev, final_prompt_index: idx }))}
                          />
                          Publish this one
                        </label>
                      </div>
                      <Textarea
                        value={prompt}
                        onChange={(e) => updatePrompt(idx, e.target.value)}
                        className="border-slate-300 bg-slate-50 text-slate-900 placeholder:text-slate-400 dark:border-[#2a2a32] dark:bg-[#111318] dark:text-white dark:placeholder:text-gray-500"
                        rows={3}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-1 block">Custom Note (optional — appears in the email above the focus prompt)</label>
                <Textarea
                  value={draftForm.custom_note}
                  onChange={(e) => setDraftForm((prev) => ({ ...prev, custom_note: e.target.value }))}
                  className="bg-[#16161a] border-[#2a2a32] text-white"
                  rows={3}
                  placeholder="Add anything you want to say to this week's recipients — a personal word, reminder, or extra context. Leave blank to skip."
                />
              </div>

              <div>
                <label className="text-sm text-gray-400 mb-2 block">Who receives this week's email?</label>
                <select
                  value={draftForm.email_audience}
                  onChange={(e) => setDraftForm((prev) => ({ ...prev, email_audience: e.target.value }))}
                  className="w-full bg-[#16161a] border border-[#2a2a32] text-white rounded-lg px-3 py-2"
                >
                  <option value="all_members">All active members</option>
                  <option value="founding_40_dad_partners">Founding 40 — Dad Partners only (9)</option>
                  <option value="founding_40_guide_partners">Founding 40 — Guide Partners only (5)</option>
                  <option value="founding_40_all">Founding 40 — Everyone (Dad Partners + Guide Partners)</option>
                </select>
              </div>

              <div className="bg-[#16161a] border border-[#2a2a32] rounded-xl p-4 text-sm text-gray-400">
                <p>Monday auto-publish fallback: <span className="text-white">12:45 AM ET</span></p>
                <p className="mt-1">Monday email send: <span className="text-white">{formatDateTime(selectedRecording.monday_email_send_at)}</span></p>
              </div>

              <div className="flex flex-wrap gap-3">
                <input
                  key={replacementPickerKey}
                  id={`recording-replacement-input-${replacementPickerKey}`}
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,audio/mpeg,audio/wav"
                  className="hidden"
                  onChange={handleReplacementFileChange}
                />
                <Button
                  onClick={chooseReplacementMedia}
                  disabled={Boolean(replacingRecordingMediaId) || deletingRecording || savingReview || publishingFocus}
                  variant="outline"
                  className="border-aqua/30 text-aqua hover:bg-aqua/10 hover:text-aqua"
                >
                  {replacingRecordingMediaId === selectedRecording.recording_id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Replace Media File
                </Button>
                <Button
                  onClick={deleteRecording}
                  disabled={deletingRecording || savingReview || publishingFocus}
                  variant="outline"
                  className="border-red-500/30 text-red-200 hover:bg-red-500/10 hover:text-red-100"
                >
                  {deletingRecording ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete Recording
                </Button>
                <Button
                  onClick={saveReview}
                  disabled={savingReview || publishingFocus || deletingRecording}
                  variant="outline"
                  className="border-slate-300 text-slate-900 hover:bg-slate-100 dark:border-[#2a2a32] dark:text-white dark:hover:bg-[#2a2a32]"
                >
                  {savingReview ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {selectedRecordingIsPublished ? 'Save Published Updates' : 'Save Draft'}
                </Button>
                <Button
                  onClick={publishFocus}
                  disabled={publishingFocus || savingReview || deletingRecording || !draftForm.candidate_prompts[draftForm.final_prompt_index]?.trim()}
                  className="bg-aqua hover:bg-aqua-dark text-charcoal"
                >
                  {publishingFocus ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {selectedRecordingIsPublished ? 'Update Published Focus' : 'Publish Weekly Focus'}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default RecordingsTab;
